// src/pages/SuperAdminOrgs.jsx

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, ChevronRight, Loader2, AlertCircle, Search, Plus, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  TableWrapper, Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';

const STATUS_STYLES = {
  active:    'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
};

const PLAN_STYLES = {
  grandfather: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  enterprise:  'bg-violet-500/20 text-violet-300 border-violet-500/30',
  custom:      'bg-neutral-500/20 text-neutral-300 border-neutral-500/30',
  solo:        'bg-amber-500/20 text-amber-300 border-amber-500/30',
  crew:        'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  studio:      'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
};

function formatLimit(current, max) {
  if (max === null || max === undefined) return `${current} · ∞`;
  return `${current} / ${max}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function SuperAdminOrgs() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/super-admin/organizations');
        if (res.ok) {
          const body = await res.json();
          setOrgs(body.organizations || []);
        } else if (res.status === 401) {
          // Session expired while on this page — the wrapper will handle re-prompting
          setError('Session expired. Verifying again...');
        } else if (res.status === 404) {
          setError('You do not have access to this page.');
        } else {
          setError('Could not load organizations.');
        }
      } catch (err) {
        console.error(err);
        setError('Network error.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = orgs.filter(o => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      o.name?.toLowerCase().includes(q) ||
      o.plan?.toLowerCase().includes(q) ||
      o.id.startsWith(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Organizations</h2>
          <p className="text-sm text-[#a0a0a0] mt-1">
            {orgs.length} total {orgs.length === 1 ? 'organization' : 'organizations'}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add organization
        </Button>
      </div>

      {/* Filter */}
      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-[#a0a0a0] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          placeholder="Filter by name, plan, or ID"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(org => (
                                    <TableRow
                    key={org.id}
                    className="group cursor-pointer hover:bg-[#1a1a1a]/50"
                    onClick={() => { window.location.href = `/super-admin/organizations/${org.id}`; }}
                  >

                    <TableCell className="font-medium text-white">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-md bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-4 w-4 text-[#a0a0a0]" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate">{org.name}</div>
                          <div className="text-xs text-[#a0a0a0] font-mono">
                            {org.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="default"
                        className={PLAN_STYLES[org.plan] || PLAN_STYLES.custom}
                      >
                        {org.plan || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="default"
                        className={STATUS_STYLES[org.status] || STATUS_STYLES.active}
                      >
                        {org.status || 'active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#e0e0e0]">
                      {formatLimit(org.active_users ?? 0, org.max_users)}
                    </TableCell>
                    <TableCell className="text-[#a0a0a0]">
                      {formatDate(org.created_at)}
                    </TableCell>
                    
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[#a0a0a0] py-8">
                      No organizations match that filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={(newId) => {
            setShowCreate(false);
            navigate(`/super-admin/organizations/${newId}`);
          }}
        />
      )}
    </div>
  );
}

// ─── Create Organization Modal ──────────────────────────────────────────

const ASSIGNABLE_PLANS = ['solo', 'crew', 'studio', 'enterprise', 'grandfather', 'custom', 'demo'];

function parseCreateLimitInput(s) {
  const trimmed = (s ?? '').trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'unlimited') return null;
  const n = parseInt(trimmed, 10);
  if (Number.isNaN(n) || n < 0) return undefined;
  return n;
}

function CreateOrgModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [plan, setPlan] = useState('grandfather');
  const [maxUsers, setMaxUsers] = useState('');
  const [maxCharts, setMaxCharts] = useState('');
  const [maxConnectors, setMaxConnectors] = useState('');
  const [trialDays, setTrialDays] = useState('');
  const [suppressEmail, setSuppressEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailWarning, setEmailWarning] = useState('');
  const [acceptanceUrl, setAcceptanceUrl] = useState('');

  const buildPayload = (emailExistsOk) => {
    const u = parseCreateLimitInput(maxUsers);
    const c = parseCreateLimitInput(maxCharts);
    const k = parseCreateLimitInput(maxConnectors);
    if (u === undefined || c === undefined || k === undefined) {
      return { error: 'Limits must be a non-negative integer or empty (= unlimited/plan default).' };
    }
    const payload = {
      name: name.trim(),
      admin_email: adminEmail.trim(),
      plan,
      suppress_email: suppressEmail,
      email_exists_ok: emailExistsOk,
    };
    if (u !== null) payload.max_users = u;
    if (c !== null) payload.max_charts = c;
    if (k !== null) payload.max_connectors = k;
    if (trialDays.trim() !== '') {
      const td = parseInt(trialDays, 10);
      if (Number.isNaN(td) || td < 0) return { error: 'Trial days must be a non-negative integer.' };
      payload.trial_days = td;
    }
    return { payload };
  };

  const submit = async (e, emailExistsOk = false) => {
    if (e) e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!adminEmail.trim()) { setError('Admin email is required.'); return; }

    const built = buildPayload(emailExistsOk);
    if (built.error) { setError(built.error); return; }

    setSubmitting(true);
    try {
      const res = await apiCall('/super-admin/organizations', {
        method: 'POST',
        body: JSON.stringify(built.payload),
      });
      if (res.ok) {
        const body = await res.json();
        const url = body.invitation?.acceptance_url;
        if (url) {
          setAcceptanceUrl(url);
        } else {
          onCreated(body.organization.id);
        }
      } else {
        const errBody = await res.json().catch(() => ({}));
        const detail = errBody.detail;
        if (res.status === 409 && detail && typeof detail === 'object'
            && detail.code === 'email_exists_elsewhere') {
          setEmailWarning(detail.message || 'This email already exists in another organization.');
        } else {
          setError(typeof detail === 'string' ? detail : 'Could not create organization.');
        }
      }
    } catch (err) {
      console.error(err);
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  if (acceptanceUrl) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="w-full max-w-lg bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl p-6 shadow-2xl">
          <h3 className="text-lg font-semibold text-white mb-2">Organization created</h3>
          <p className="text-sm text-[#a0a0a0] mb-4">
            Email was suppressed. Send this acceptance link to the admin so they
            can set their password. It is shown once.
          </p>
          <div className="p-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg break-all text-xs text-[#e0e0e0] font-mono mb-4">
            {acceptanceUrl}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => navigator.clipboard?.writeText(acceptanceUrl)}>
              Copy link
            </Button>
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <h3 className="text-lg font-semibold text-white">Create organization</h3>
          <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" disabled={submitting} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={(e) => submit(e, false)} className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} disabled={submitting} required />
          </div>

          <div>
            <Label>Admin email</Label>
            <Input
              type="email"
              value={adminEmail}
              onChange={e => { setAdminEmail(e.target.value); setEmailWarning(''); }}
              placeholder="They'll get an invite to set their password"
              disabled={submitting}
              required
            />
          </div>

          <div>
            <Label>Plan</Label>
            <select
              value={plan}
              onChange={e => setPlan(e.target.value)}
              disabled={submitting}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-white"
            >
              {ASSIGNABLE_PLANS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <p className="text-xs text-[#a0a0a0] mt-1">
              Leave limits empty to use the plan's defaults (grandfather/enterprise = unlimited).
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Max users</Label>
              <Input value={maxUsers} onChange={e => setMaxUsers(e.target.value)} placeholder="Default" disabled={submitting} />
            </div>
            <div>
              <Label>Max charts</Label>
              <Input value={maxCharts} onChange={e => setMaxCharts(e.target.value)} placeholder="Default" disabled={submitting} />
            </div>
            <div>
              <Label>Max connectors</Label>
              <Input value={maxConnectors} onChange={e => setMaxConnectors(e.target.value)} placeholder="Default" disabled={submitting} />
            </div>
          </div>

          <div>
            <Label>Trial days from now (optional)</Label>
            <Input
              type="number" min={0} value={trialDays}
              onChange={e => setTrialDays(e.target.value)}
              placeholder="Leave empty for no trial"
              disabled={submitting}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-[#a0a0a0] cursor-pointer">
            <input
              type="checkbox"
              checked={suppressEmail}
              onChange={e => setSuppressEmail(e.target.checked)}
              disabled={submitting}
              className="rounded border-[#2a2a2a]"
            />
            Don't send the invite email (show me the link instead)
          </label>

          {emailWarning && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-300 mb-2">{emailWarning}</p>
              <Button type="button" variant="ghost" disabled={submitting}
                      onClick={() => submit(null, true)}
                      className="text-amber-300 hover:text-amber-200">
                Proceed anyway
              </Button>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#2a2a2a]">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create organization'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}